<script lang="ts" setup>
  import { useMutation, useQuery } from '@tanstack/vue-query'
  import { format, parse } from 'date-fns'

  import { BngHoleNumberTag } from '~/components/golf'
  import { DATE_FORMAT_LITE, TIME_SLOTS_CLOCK } from '~/core/constants'
  import { useCoursesService, useOrderService } from '~/core/services'
  import { type ButtonInputOption, type DateString, type FetchErrorObj, type Nullable, TimeSlots } from '~/core/types'
  import type { Course } from '~/core/types/golfs'
  import type { PostBookingsDTO } from '~/core/types/order'
  import { BngModal } from '~/layouts/components'

  import Availability from './Availability.vue'

  type Props = {
    courses: Course[]
    embed?: boolean
    golfName: string
  }

  const props = defineProps<Props>()

  const router = useRouter()
  const { t } = useI18n()
  const { displayToast } = useUiStore()
  const { postBookings } = useOrderService()
  const { refetchCart } = useCartStore()
  const { postCourseAvailabilities } = useCoursesService()
  const { filters, updateQuery, timeSlotArray } = useFilters(false, ['date', 'players', 'slots'])
  const { matches: matchDesktop } = useMatchMedia(ScreenWidth.desktop)

  const displayDateInputDropdown = ref(false)
  const selectedCourseId = ref<Nullable<number>>(null)
  const selectedSlotId = ref<Nullable<number>>(null)

  const timeSlotsList = ref<ButtonInputOption<TimeSlots>[]>([
    {
      label: 'Matin',
      value: TimeSlots.MATIN,
    },
    {
      label: 'Midi',
      value: TimeSlots.MIDI,
    },
    {
      label: 'Après-midi',
      value: TimeSlots.APRES_MIDI,
    },
  ])

  const containerRef = useTemplateRef<HTMLDivElement>('content')
  const filtersRef = useTemplateRef<HTMLDivElement>('filters')
  const dateModal = ref(false)

  const sortedCourses = computed(() => props.courses.toSorted((a, b) => b.holes - a.holes))

  const selectedTimeSlot = computed(() => {
    if (!filters.value.time_slots) {
      return []
    } else if (!Array.isArray(filters.value.time_slots)) {
      return [filters.value.time_slots]
    } else {
      return filters.value.time_slots
    }
  })

  function handleDateInput(d: DateString) {
    updateQuery({ date: d })
    displayDateInputDropdown.value = false
    dateModal.value = false
  }

  function onPlayerNumberInput(players: number) {
    updateQuery({ players })
  }

  function onCourseInput(id: number) {
    selectedCourseId.value = id
  }

  function onTimeSlotsInput(ts: TimeSlots[]) {
    updateQuery({
      time_slots: ts[0],
    })
  }

  function onAvailabilityClick(id: number) {
    selectedSlotId.value = id
  }

  const currentDateLabel = computed(() => format(parse(filters.value.date, DATE_FORMAT_LITE, new Date()), 'dd/MM/yyyy'))

  const canFetchAvailabilities = computed(() => selectedCourseId.value !== null)
  const { data: courseAvailabilities } = useQuery({
    enabled: canFetchAvailabilities,
    queryKey: ['course', 'availabilities', filters, selectedCourseId],
    queryFn: () => postCourseAvailabilities(selectedCourseId.value ?? 0, {
      date_start: `${timeSlotArray.value[0].start}`,
      date_end: `${timeSlotArray.value[0].end}`,
      has_promotions: false,
      players: filters.value.players,
    }),
  })

  const { mutate: addToCart } = useMutation<unknown, FetchErrorObj<PostBookingsDTO>>({
    mutationFn: () => postBookings({ players: filters.value.players, slot_id: selectedSlotId.value }),
    onSuccess: async () => {
      refetchCart()
      await router.push({ name: 'shopping-cart-basket' })
    },
    onError: (res) => {
      displayToast(res.message ?? 'Une erreur est survenue.', 'error')
    },
  })

  const defaultTimeSlot = computed(() => {
    const today = format(new Date(), DATE_FORMAT_LITE)
    if (filters.value.date !== today) {
      return TimeSlots.MATIN
    }
    const currentTime = format(new Date(), 'HH:mm:ss')
    const isAfterMorning = currentTime > TIME_SLOTS_CLOCK.matin[1]
    const isBeforeAfternoon = currentTime < TIME_SLOTS_CLOCK[TimeSlots.APRES_MIDI][0]
    if (isAfterMorning && isBeforeAfternoon) {
      return TimeSlots.MIDI
    } else if (currentTime > TIME_SLOTS_CLOCK.midi[1]) {
      return TimeSlots.APRES_MIDI
    } else {
      return TimeSlots.MATIN
    }
  })

  function displayNextSlots() {
    if (containerRef.value && filtersRef.value) {
      containerRef.value.scrollTo({ top: filtersRef.value.clientHeight, behavior: 'smooth' })
    }
    if (!filters.value.time_slots || filters.value.time_slots === TimeSlots.APRES_MIDI) {
      return
    }
    updateQuery({
      time_slots: filters.value.time_slots === TimeSlots.MATIN ? TimeSlots.MIDI : TimeSlots.APRES_MIDI,
    })
  }

  function handleDateInputClick() {
    dateModal.value = true
  }

  watch(() => props.courses, (c) => {
    selectedCourseId.value = c.find((_, i) => i === 0)?.id ?? null
    updateQuery({
      time_slots: defaultTimeSlot.value,
    })
  }, { immediate: true })

  watch(() => filters.value.date, () => {
    displayDateInputDropdown.value = false
  })
</script>

<template>
  <div
    class="c-golf-detail-reservation"
    :class="{ embed: props.embed }"
  >
    <Teleport
      v-if="!matchDesktop && dateModal"
      to="#modal"
    >
      <BngModal
        :hide-backdrop="true"
        closable
        @close="dateModal = false"
      >
        <DatePicker
          :model-value="filters.date"
          @update:model-value="handleDateInput"
        />
      </BngModal>
    </Teleport>

    <div class="top">
      <div class="title">
        {{ t("golf.reservation-title") }}
      </div>
      <div class="bottom">
        <div class="wrapper">
          <SvgIcon icon="pointer" />
          <div class="subtitle">
            <div class="name">
              {{ props.golfName }}
            </div>
          </div>
        </div>
        <div>
          <div class="tag">
            Green fee
          </div>
        </div>
      </div>
    </div>
    <div
      ref="content"
      class="content"
    >
      <div
        ref="filters"
        class="filters"
      >
        <div class="col">
          <div class="field">
            <p class="label">
              <SvgIcon
                :width="20"
                :height="18"
                :desktop-width="20"
                :desktop-height="18"
                icon="calendar"
              />
              <span>Date</span>
            </p>
            <InputBase
              v-if="!matchDesktop"
              class="when"
              icon="calendar"
              :placeholder="`${t('common.when')} ?`"
              name="when"
              :model-value="currentDateLabel"
              :readonly="true"
              @click.stop="handleDateInputClick"
            />
            <InputDropdown
              v-else
              :model-value="currentDateLabel"
              :dropdown-displayed="displayDateInputDropdown"
              name="when-dropdown"
              :input-props="{
                resetable: true,
                icon: 'calendar',
                placeholder: `${t('common.when')} ?`,
                name: 'when',
              }"
              @input-click="displayDateInputDropdown = true"
              @click-outside="displayDateInputDropdown = false"
            >
              <DatePicker
                :model-value="filters.date"
                @update:model-value="handleDateInput"
              />
            </InputDropdown>
          </div>
          <div class="field">
            <p class="label">
              <SvgIcon
                :width="20"
                :height="20"
                :desktop-width="20"
                :desktop-height="20"
                icon="player"
              />
              <span>Joueurs</span>
            </p>
            <InputPlayerNumber
              :model-value="filters.players"
              @update:model-value="onPlayerNumberInput"
            />
          </div>
        </div>
        <div class="col">
          <div class="field">
            <p class="label">
              <SvgIcon
                :width="12"
                :height="18"
                :desktop-width="12"
                :desktop-height="18"
                icon="hole"
              />
              <span>Parcours</span>
            </p>
            <div class="courses-list">
              <div
                v-for="(c, index) in sortedCourses"
                :key="index"
                class="course"
                @click="() => onCourseInput(c.id)"
              >
                <span
                  class="name"
                  :class="{ selected: c.id === selectedCourseId }"
                >
                  {{ c.name }}
                </span>
                <BngHoleNumberTag
                  :hole-number="c.holes"
                  :selected="c.id === selectedCourseId"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="slots">
        <div class="field">
          <p class="label">
            <SvgIcon
              :width="12"
              :height="18"
              :desktop-width="12"
              :desktop-height="18"
              icon="hole"
            />
            <span>Horaires de départ</span>
          </p>
          <InputButtons
            :model-value="selectedTimeSlot"
            :options="timeSlotsList"
            :width="80"
            :multiple="false"
            @update:model-value="onTimeSlotsInput"
          >
            <template #option="{ option }">
              <span class="buttons-option">
                {{ option.label }}
              </span>
            </template>
          </InputButtons>
        </div>
        <div
          v-if="courseAvailabilities?.data?.length"
          class="availabilities"
        >
          <Availability
            v-for="availability in courseAvailabilities.data"
            :key="availability.id"
            :availability="availability"
            :selected-id="selectedSlotId"
            @click="() => onAvailabilityClick(availability.id)"
          />
        </div>
        <div
          v-else
          class="no-available-slots"
        >
          <span>Pas de créneau disponible</span>
        </div>
        <div
          v-if="filters.time_slots && !filters.time_slots.includes(TimeSlots.APRES_MIDI)"
          class="show-next"
        >
          <a @click="displayNextSlots">Voir les départs suivants</a>
        </div>
      </div>
    </div>
    <div class="footer">
      <button
        class="add-to-cart"
        :disabled="!selectedSlotId"
        @click="() => addToCart()"
      >
        <span class="title">
          Réserver
          <SvgIcon
            icon="arrow-nav"
            :width="24"
            :height="20"
          />
        </span>
      </button>
    </div>
  </div>
</template>

<style scoped>
.c-golf-detail-reservation {
  display: flex;
  overflow: hidden;
  width: 100%;
  flex: 1 1 auto;
  flex-direction: column;

  .top {
    display: flex;
    min-height: 100px;
    flex: 0 0 auto;
    flex-direction: column;
    justify-content: space-evenly;
    padding: 0.75em 1.5em;
    background-color: var(--color-primary);

    .title {
      color: white;
      font-family: Bariol, sans-serif;
      font-size: 1.25em;
      font-weight: bold;
      letter-spacing: 1.5px;
      text-transform: uppercase;
    }

    .bottom {
      display: flex;
      align-items: flex-end;
      justify-content: space-between;

      .wrapper {
        display: flex;
        margin-top: 0.75em;
        margin-right: 0.5em;

        .c-icon {
          margin-right: 1em;
          opacity: 0.5;
        }

        .subtitle {
          .name {
            color: var(--color-secondary);
            font-family: Open-Sans, sans-serif;
            font-size: 1.125em;
            font-weight: bold;

          }

          .location {
            color: var(--color-secondary);
            font-family: Open-Sans, sans-serif;
            font-size: 1em;
            font-weight: 600;
          }
        }
      }

      .tag {
        padding: 0.375em 0.5em;
        border-radius: 0.25em;
        background-color: var(--color-secondary-light);
        color: var(--color-secondary);
        font-family: Open-Sans, sans-serif;
        font-size: 1em;
        font-weight: 600;
        white-space: nowrap;
      }
    }
  }

  .content {
    display: block;
    overflow: visible;
    max-height: calc(100vh - var(--total-height) - 1.5em);
    flex: 1 1 auto;
    padding: 2em 3em 0;
    background-color: white;
    overflow-y: scroll;

    --footer-height: 0px;
    --header-height: 100px;
    --breadcrumb-height: 0px;
    --menu-height: 62px;
    --total-height: calc(
      var(--footer-height) + var(--header-height) + var(--breadcrumb-height) + var(--menu-height)
    );

    .field {
      .when.c-input {
        width: 100%;
        margin-bottom: 1em;

        &:deep(input) {
          &[readonly] {
            padding: 0.625em;
            background-color: var(--color-secondary-light);
          }
        }
      }

      flex: 1 1 auto;

      .label {
        display: flex;
        flex-direction: row;
        margin-bottom: 0.875em;

        span {
          color: var(--color-secondary);
          font-family: Open-Sans, sans-serif;
          font-size: 1em;
          font-weight: bold;
          opacity: 0.8;
        }

        .c-icon {
          margin-right: 0.5em;
          color: var(--color-primary);
          opacity: 1;
        }
      }

      .c-input-dropdown {
        font-size: 0.875em;
      }

      .c-buttons-input {
        font-size: 1.25em;
      }
    }

    .filters {
      display: flex;
      flex: 1 1 auto;
      flex-direction: column;

      .col {
        display: flex;
        flex: 1 1 50%;
        flex-direction: column;

        .field {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          margin-bottom: 2em;

          .courses-list {
            width: 100%;

            .course {
              display: flex;
              align-items: center;
              justify-content: space-between;
              border-radius: 0.25em;
              margin: 0 1.5em 0.5em;
              cursor: pointer;

              .name {
                color: var(--color-secondary);
                cursor: pointer;
                font-family: Open-Sans, sans-serif;
                font-size: 0.875em;
                font-weight: normal;
                opacity: 0.8;

                &.selected {
                  font-weight: bold;
                }
              }
            }
          }
        }
      }
    }

    .slots {
      display: flex;
      flex-direction: column;

      .field {
        .c-buttons-input {
          font-size: 1em;

          .buttons-option {
            font-size: 0.875em;
          }
        }
      }

      .availabilities {
        display: grid;
        padding: 0.5em;
        margin: 0.5em -0.5em 0;
        grid-gap: 0.75em;
        grid-template-columns: 1fr;
      }

      .no-available-slots {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 1em;

        span {
          color: var(--color-secondary);
          font-family: Open-Sans, sans-serif;
          font-size: 0.875em;
          font-weight: 500;
          opacity: 0.7;
        }
      }

      .show-next {
        display: flex;
        align-items: flex-end;
        justify-content: flex-end;
        padding: 1em;

        a {
          color: var(--color-primary);
          cursor: pointer;
          font-family: Open-Sans, sans-serif;
          font-size: 0.875em;
          font-weight: 500;
          text-decoration: underline;
        }
      }
    }

    .c-divider {
      margin: 0 1.5em;
    }
  }

  .footer {
    height: 90px;
    flex: 0 0 auto;
    padding: 1.25em 3em;
    background: white;
    box-shadow: var(--box-shadow-1);
  }

  .add-to-cart {
    display: flex;
    width: 225px;
    height: 100%;
    flex: 0 0 auto;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    padding: 0 1em;
    background-color: var(--color-primary);
    cursor: pointer;
    transition: opacity 0.25s ease-out;

    &:hover {
      .title {
        .c-icon {
          margin-right: 0;
        }
      }
    }

    &[disabled] {
      cursor: not-allowed;
      opacity: 0.25;
    }

    .title {
      display: flex;
      flex: 1 1 auto;
      flex-direction: row;
      justify-content: space-between;
      color: var(--color-secondary);
      font-family: Bariol, sans-serif;
      font-size: 1.375em;
      font-weight: bold;
      letter-spacing: 1.5px;
      text-transform: uppercase;

      .c-icon {
        margin-right: 0.25em;
        transform: rotate(-0.25turn);
        transition: margin-right ease-out 0.25s;
      }

      &.small {
        color: var(--color-secondary);
        font-family: Open-Sans, sans-serif;
        font-size: 0.875em;
        font-weight: 500;
        letter-spacing: normal;
        text-transform: none;
      }
    }
  }

  @media screen and (width >= 600px) {
    &.embed {
      .content {
        .filters {
          flex-direction: row;
        }
      }
    }
  }

  @media screen and (width >= 1024px) {
    overflow: visible;
    height: auto;
    max-height: 100%;
    flex: 0 0 auto;
    align-self: self-start;

    .top {
      min-height: auto;
    }

    .content {
      --footer-height: 90px;
      --header-height: 86px;
      --breadcrumb-height: 80px;
      --menu-height: 90px;

      .filters {
        flex-direction: row;

        .col {
          &:last-child {
            margin-left: 2em;
          }
        }
      }

      .slots {
        .availabilities {
          overflow: auto;
          max-height: calc(
            var(--slot-height) * var(--displayed-slots-rows) +
            ((var(--displayed-slots-rows) - 1) * 0.75em) + 1em
          );
          grid-template-columns: 1fr 1fr;

          --slot-height: 60px;
          --displayed-slots-rows: 4;
        }
      }
    }
  }
}
</style>
